import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, InputNumber, message, Divider, Typography } from 'antd';
import { adminTasksApi } from '../../../services/api/adminTasks';
import { AdminTaskType, AdminTaskStatus } from '../../../types';

const { TextArea } = Input;
const { Text } = Typography;

interface AutoChapterConfigModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  storyId: string;
  storyName: string;
  initialValues?: { chapterName?: string; instruction?: string };
}

const AutoChapterConfigModal: React.FC<AutoChapterConfigModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  storyId,
  storyName,
  initialValues,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible) {
      if (initialValues) {
        form.setFieldsValue({
          chapterName: initialValues.chapterName,
          instruction: initialValues.instruction,
        });
      } else {
        form.resetFields();
      }
    }
  }, [visible, initialValues, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const detail = {
        input: {
          sourceStoryId: storyId,
          storyName: storyName,
          chapterName: values.chapterName,
          instruction: values.instruction,
          outlinePassScore: values.outlinePassScore,
          maxOutlineRetries: values.maxOutlineRetries,
          contentPassScore: values.contentPassScore,
          maxContentRetries: values.maxContentRetries,
        },
        progress: 0,
        targetId: storyId,
      };

      await adminTasksApi.createTask({
        type: AdminTaskType.AUTO_CHAPTER,
        detail: detail as any,
      });

      message.success('Đã khởi tạo tiến trình Auto Chapter thành công!');
      form.resetFields();
      onSuccess();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      console.error('Failed to create auto chapter task:', error);
      message.error('Không thể khởi tạo tiến trình: ' + (error.message || 'Lỗi không xác định'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={<span>📝 Tự động tạo Chapter - <b>{storyName}</b></span>}
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={600}
      okText="Bắt đầu"
      cancelText="Hủy"
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          outlinePassScore: 85,
          maxOutlineRetries: 5,
          contentPassScore: 85,
          maxContentRetries: 5,
        }}
      >
        <Divider orientation={"left" as any}>Thông tin Chapter</Divider>
        <Form.Item
          name="chapterName"
          label="Tên Chapter (Để trống nếu muốn AI tự đặt)"
        >
          <Input placeholder="Ví dụ: Chương 2: Cuộc hội ngộ bất ngờ" />
        </Form.Item>

        <Form.Item
          name="instruction"
          label="Chỉ dẫn nội dung (Prompt định hướng)"
        >
          <TextArea 
            rows={4} 
            placeholder="Mô tả sơ qua về nội dung hoặc các tình tiết quan trọng AI cần tuân thủ..." 
          />
        </Form.Item>

        <Divider orientation={"left" as any}>Cấu hình Đánh giá & Retry</Divider>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Form.Item
            name="outlinePassScore"
            label="Ngưỡng điểm Outline"
            tooltip="Điểm AI chấm để chấp nhận bản Outline"
          >
            <InputNumber min={1} max={100} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="maxOutlineRetries"
            label="Số lần thử Outline tối đa"
          >
            <InputNumber min={1} max={10} style={{ width: '100%' }} />
          </Form.Item>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Form.Item
            name="contentPassScore"
            label="Ngưỡng điểm Content"
            tooltip="Điểm AI chấm để chấp nhận bản Content"
          >
            <InputNumber min={1} max={100} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="maxContentRetries"
            label="Số lần thử Content tối đa"
          >
            <InputNumber min={1} max={10} style={{ width: '100%' }} />
          </Form.Item>
        </div>

        <Text type="secondary" style={{ fontSize: '12px' }}>
          * Hệ thống sẽ tự động tạo Chapter mới và thực hiện Review Loop để đảm bảo chất lượng.
        </Text>
      </Form>
    </Modal>
  );
};

export default AutoChapterConfigModal;
